import React, { useState } from "react";
import {
  Upload,
  FileText,
  Search,
  Filter,
  Download,
  Trash2,
  Eye,
  Plus,
  Folder,
  FolderOpen,
  Calendar,
  User,
  Tag,
  MoreVertical,
  Grid,
  List,
  SortAsc,
  SortDesc,
  X,
} from "lucide-react";
import { useFiles } from "../context/FileContext";
import { FileRecord } from "../context/FileContext";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import Input from "../components/ui/Input";
import FileUpload from "../components/ui/FileUpload";
import FileBrowser from "../components/ui/FileBrowser";
import Badge from "../components/ui/Badge";
import { format } from "date-fns";

export default function Documents() {
  const { state } = useFiles();
  const [activeTab, setActiveTab] = useState<"browse" | "upload">("browse");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [sortBy, setSortBy] = useState<"name" | "date" | "size">("date");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [selectedFile, setSelectedFile] = useState<FileRecord | null>(null);

  const categories = [
    { id: "all", name: "All Documents", count: state.files.length },
    {
      id: "governance",
      name: "Governance",
      count: state.files.filter((f) => f.category === "governance").length,
    },
    {
      id: "membership",
      name: "Membership",
      count: state.files.filter((f) => f.category === "membership").length,
    },
    {
      id: "finance",
      name: "Finance",
      count: state.files.filter((f) => f.category === "finance").length,
    },
    {
      id: "legal",
      name: "Legal",
      count: state.files.filter((f) => f.category === "legal").length,
    },
    {
      id: "media",
      name: "Media",
      count: state.files.filter((f) => f.category === "media").length,
    },
    {
      id: "admin",
      name: "Administration",
      count: state.files.filter((f) => f.category === "admin").length,
    },
  ];

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const getFileIcon = (file: FileRecord) => {
    const type = file.type.toLowerCase();
    if (type.includes("pdf")) return <FileText className="h-8 w-8" />;
    if (type.includes("image")) return "🖼️";
    if (type.includes("video")) return "🎥";
    if (type.includes("audio")) return "🎵";
    if (type.includes("zip") || type.includes("rar")) return "📦";
    if (type.includes("excel") || type.includes("spreadsheet")) return "📊";
    if (type.includes("word") || type.includes("document")) return "📄";
    return "📄";
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Document Management
          </h1>
          <p className="text-gray-600">
            Manage and organize all syndicate documents
          </p>
        </div>
        <div className="flex space-x-3">
          <Button
            variant={activeTab === "browse" ? "primary" : "outline"}
            onClick={() => setActiveTab("browse")}
          >
            <Eye className="h-4 w-4 mr-2" />
            Browse
          </Button>
          <Button
            variant={activeTab === "upload" ? "primary" : "outline"}
            onClick={() => setActiveTab("upload")}
          >
            <Upload className="h-4 w-4 mr-2" />
            Upload
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <div className="flex items-center">
            <div className="p-3 bg-blue-100 rounded-lg">
              <FileText className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Files</p>
              <p className="text-2xl font-bold text-gray-900">
                {state.files.length}
              </p>
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center">
            <div className="p-3 bg-green-100 rounded-lg">
              <Download className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">
                Total Downloads
              </p>
              <p className="text-2xl font-bold text-gray-900">
                {state.files.reduce((sum, f) => sum + f.downloadCount, 0)}
              </p>
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center">
            <div className="p-3 bg-purple-100 rounded-lg">
              <Folder className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Categories</p>
              <p className="text-2xl font-bold text-gray-900">
                {categories.filter((c) => c.id !== "all" && c.count > 0).length}
              </p>
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center">
            <div className="p-3 bg-orange-100 rounded-lg">
              <Calendar className="h-6 w-6 text-orange-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">This Month</p>
              <p className="text-2xl font-bold text-gray-900">
                {
                  state.files.filter((f) => {
                    const fileDate = new Date(f.uploadedAt);
                    const now = new Date();
                    return (
                      fileDate.getMonth() === now.getMonth() &&
                      fileDate.getFullYear() === now.getFullYear()
                    );
                  }).length
                }
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Categories */}
      <Card>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Categories</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
          {categories.map((category) => (
            <button
              key={category.id}
              className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <div className="text-2xl mb-2">
                {category.id === "all" ? "📁" : "📂"}
              </div>
              <div className="text-center">
                <p className="text-sm font-medium text-gray-900">
                  {category.name}
                </p>
                <p className="text-xs text-gray-500">{category.count} files</p>
              </div>
            </button>
          ))}
        </div>
      </Card>

      {/* Main Content */}
      {activeTab === "browse" ? (
        <FileBrowser
          category="all"
          onFileSelect={setSelectedFile}
          showUpload={false}
        />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Upload Documents
            </h3>
            <FileUpload
              category="admin"
              description="General administrative documents"
              onUploadComplete={(fileId) => {
                setActiveTab("browse");
              }}
            />
          </Card>

          <Card>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Upload Guidelines
            </h3>
            <div className="space-y-4 text-sm text-gray-600">
              <div>
                <h4 className="font-medium text-gray-900 mb-2">
                  Supported Formats
                </h4>
                <ul className="space-y-1">
                  <li>• PDF documents (.pdf)</li>
                  <li>• Microsoft Office (.docx, .xlsx, .pptx)</li>
                  <li>• Images (.jpg, .png, .gif)</li>
                  <li>• Archives (.zip, .rar)</li>
                </ul>
              </div>
              <div>
                <h4 className="font-medium text-gray-900 mb-2">
                  File Size Limits
                </h4>
                <ul className="space-y-1">
                  <li>• Maximum file size: 10MB</li>
                  <li>• Recommended: Under 5MB for faster uploads</li>
                </ul>
              </div>
              <div>
                <h4 className="font-medium text-gray-900 mb-2">
                  Best Practices
                </h4>
                <ul className="space-y-1">
                  <li>• Use descriptive file names</li>
                  <li>• Add meaningful descriptions</li>
                  <li>• Categorize files appropriately</li>
                  <li>• Keep sensitive documents private</li>
                </ul>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* File Details Modal */}
      {selectedFile && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                File Details
              </h3>
              <button
                onClick={() => setSelectedFile(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="flex items-center space-x-4">
                <div className="text-4xl">{getFileIcon(selectedFile)}</div>
                <div>
                  <h4 className="text-lg font-medium text-gray-900">
                    {selectedFile.name}
                  </h4>
                  <p className="text-sm text-gray-500">
                    {formatFileSize(selectedFile.size)}
                  </p>
                </div>
              </div>

              {selectedFile.description && (
                <div>
                  <h5 className="font-medium text-gray-900 mb-1">
                    Description
                  </h5>
                  <p className="text-sm text-gray-600">
                    {selectedFile.description}
                  </p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h5 className="font-medium text-gray-900 mb-1">Category</h5>
                  <Badge variant="info">{selectedFile.category}</Badge>
                </div>
                <div>
                  <h5 className="font-medium text-gray-900 mb-1">Visibility</h5>
                  <Badge
                    variant={selectedFile.isPublic ? "success" : "warning"}
                  >
                    {selectedFile.isPublic ? "Public" : "Private"}
                  </Badge>
                </div>
                <div>
                  <h5 className="font-medium text-gray-900 mb-1">Uploaded</h5>
                  <p className="text-sm text-gray-600">
                    {format(
                      new Date(selectedFile.uploadedAt),
                      "MMM dd, yyyy 'at' HH:mm"
                    )}
                  </p>
                </div>
                <div>
                  <h5 className="font-medium text-gray-900 mb-1">Downloads</h5>
                  <p className="text-sm text-gray-600">
                    {selectedFile.downloadCount}
                  </p>
                </div>
              </div>

              {selectedFile.tags.length > 0 && (
                <div>
                  <h5 className="font-medium text-gray-900 mb-2">Tags</h5>
                  <div className="flex flex-wrap gap-2">
                    {selectedFile.tags.map((tag, index) => (
                      <Badge key={index} variant="default" size="sm">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex space-x-3 pt-4 border-t border-gray-200">
                <Button
                  onClick={() => {
                    // Download functionality would be implemented here
                    setSelectedFile(null);
                  }}
                  className="flex-1"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setSelectedFile(null)}
                  className="flex-1"
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
